import { t } from "@lingui/macro";
import React from "react";

import { Button } from "../../controls/Button/Button";
import {
  AlertIcon,
  InfoIcon,
  CheckIcon,
  CloseIcon,
} from "../../icons_v2/general";
import {
  Box,
  HStack,
  VStack,
  Text,
  Pressable,
  IconProps,
} from "../../primitive";
import { ToastProps } from "./types";

export const Toast = ({
  semanticStyle,
  title,
  description,
  rightButton,
  percentageComplete = 0,
  id,
  canDismiss,
  closeToast,
}: ToastProps) => {
  const iconProps: IconProps = {
    color: "grayscale-1000",
  };
  return (
    <Box
      pointerEvents="box-none"
      borderRadius="2"
      flexDirection={"row"}
      width="100%"
      maxWidth={400}
      //@ts-ignore
      fsClass="fs-unmask"
      shadowColor={"black"}
      shadowOffset={{ width: 0, height: 4 }}
      shadowOpacity={0.08}
      shadowRadius={16}
      elevation={2}
      testID={id}
    >
      <Box
        pointerEvents="none"
        testID="toast-container"
        borderTopLeftRadius="2"
        borderBottomLeftRadius="2"
        borderTopRightRadius="0"
        borderBottomRightRadius="0"
        backgroundColor={
          semanticStyle === "info"
            ? "surface-secondary"
            : `surface-status-${semanticStyle}-subtle`
        }
        px={2}
        overflow="hidden"
        justifyContent="center"
        flexGrow={0}
        flexBasis={32}
        flexShrink={0}
      >
        <Box
          position="absolute"
          left={0}
          right={0}
          height={`${percentageComplete}%`}
          bottom={0}
          backgroundColor={
            semanticStyle === "info"
              ? "grayscale-700"
              : `icon-status-${semanticStyle}`
          }
          zIndex={10}
        />
        {semanticStyle === "error" ? (
          <Box style={{ zIndex: 20 }}>
            <AlertIcon {...iconProps} testID="error-icon" />
          </Box>
        ) : null}
        {semanticStyle === "warning" ? (
          <Box style={{ zIndex: 20 }}>
            <AlertIcon {...iconProps} testID="warning-icon" />
          </Box>
        ) : null}
        {semanticStyle === "success" ? (
          <Box style={{ zIndex: 20 }}>
            <CheckIcon {...iconProps} testID="success-icon" />
          </Box>
        ) : null}
        {semanticStyle === "info" ? (
          <Box style={{ zIndex: 20 }}>
            <InfoIcon {...iconProps} testID="info-icon" />
          </Box>
        ) : null}
      </Box>
      <Box
        pointerEvents="box-none"
        padding={4}
        borderWidth={0}
        borderTopRightRadius="4"
        borderBottomRightRadius="4"
        backgroundColor="grayscale-0"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        position="relative"
        flex={1}
        minWidth={0}
      >
        <HStack
          pointerEvents="box-none"
          flex={1}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={1}
          minWidth={0}
        >
          <VStack flex={1} flexShrink={1} gap={1} minWidth={0}>
            {title ? (
              <Box testID="toast-title-container" style={{ flexShrink: 1 }}>
                <Text
                  pointerEvents="none"
                  variant="mainSmallSemiBold"
                  style={{ flexShrink: 1 }}
                >
                  {title}
                </Text>
              </Box>
            ) : null}
            {description ? (
              <Box
                testID="toast-description-container"
                style={{ flexShrink: 1 }}
              >
                <Text
                  pointerEvents="none"
                  color={"text-secondary"}
                  variant="mainTiny"
                  style={{ flexShrink: 1 }}
                >
                  {description}
                </Text>
              </Box>
            ) : null}
          </VStack>
          {rightButton ? (
            <VStack>
              <Button
                variant="ghost"
                size="tiny"
                onPress={() => {
                  rightButton.onPress();
                  id && rightButton.shouldOnPressDismiss && closeToast?.(id);
                }}
                accessibilityLabel={t`Toast Right Button-${rightButton.label}`}
              >
                {rightButton.label}
              </Button>
            </VStack>
          ) : null}
          {canDismiss && id ? (
            <VStack>
              <Pressable
                onPress={() => closeToast?.(id)}
                justifyContent="center"
                accessibilityLabel={t`Toast Close Button`}
              >
                <CloseIcon />
              </Pressable>
            </VStack>
          ) : null}
        </HStack>
      </Box>
    </Box>
  );
};
